import { useEffect, useRef, useState, useCallback } from 'react'
import { ToolCallDisplay } from './ToolCallDisplay'
import { Markdown } from './Markdown'
import { TypingIndicator } from './TypingIndicator'
import { ScrollButton } from './ScrollButton'
import { ChatEmptyState } from './ChatEmptyState'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type {
  Message as MessageType,
  MessageContent,
  MaxIterationsPart,
  ContextLengthPart,
  OutputTruncatedPart,
} from '@/store/chat/chatStore'
import { Bot, User, Copy, Check, AlertCircle, Scissors, RotateCcw } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

interface MessageListProps {
  messages: MessageType[]
  streamingContent: MessageContent[]
  isStreaming: boolean
  failedMessageId?: string | null
  errorMessage?: string | null
  onRetry?: (messageId: string) => void
}

export function MessageList({
  messages,
  streamingContent,
  isStreaming,
  failedMessageId,
  errorMessage,
  onRetry,
}: MessageListProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const handleCopyMessage = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content)
      setCopiedMessageId(messageId)
      toast.success('Message copied to clipboard', {
        icon: <Copy className="w-4 h-4" />,
      })
      setTimeout(() => setCopiedMessageId(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
      toast.error('Failed to copy message')
    }
  }

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages, streamingContent, scrollToBottom])

  // Handle scroll to show/hide scroll button
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100
    setShowScrollButton(!isNearBottom)
  }, [])

  return (
    <div className="relative h-full">
      <ScrollArea className="h-full w-full" onScrollCapture={handleScroll} ref={scrollAreaRef}>
        <div className="w-full">
          {/* Empty state */}
          {messages.length === 0 && streamingContent.length === 0 && <ChatEmptyState />}

          {/* Messages */}
          {messages.map((message) => (
            <div
              key={message.uuid}
              className={cn(
                'group transition-colors',
                message.role === 'user' ? 'bg-transparent' : 'bg-muted/20',
              )}
            >
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
                <div className="flex gap-4 items-start">
                  {/* Avatar */}
                  <Avatar className="h-8 w-8 shrink-0 mt-0.5">
                    <AvatarFallback
                      className={cn(
                        'text-xs font-medium',
                        message.role === 'user'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-secondary text-secondary-foreground',
                      )}
                    >
                      {message.role === 'user' ? (
                        <User className="h-4 w-4" />
                      ) : (
                        <Bot className="h-4 w-4" />
                      )}
                    </AvatarFallback>
                  </Avatar>

                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-2">
                    {/* Role label */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {message.role === 'user' ? 'You' : 'EcoSemantic'}
                      </span>
                    </div>

                    {/* Message content */}
                    {message.parts && message.parts.length > 0 ? (
                      <div className="space-y-3">
                        {message.parts.map((part, partIndex) => (
                          <div key={partIndex}>
                            {part.type === 'text' && (
                              <div className="prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed prose-pre:bg-muted prose-pre:border prose-pre:border-border">
                                <Markdown>{part.text}</Markdown>
                              </div>
                            )}
                            {part.type === 'tool-invocation' && (
                              <ToolCallDisplay
                                toolCallId={part.toolInvocation.toolCallId}
                                toolName={part.toolInvocation.toolName}
                                arguments={JSON.stringify(part.toolInvocation.args)}
                                result={JSON.stringify(part.toolInvocation.result)}
                                success={part.toolInvocation.state === 'result'}
                                isExecuting={false}
                              />
                            )}
                            {part.type === 'max-iterations' &&
                              (() => {
                                const maxIterPart = part as MaxIterationsPart
                                return (
                                  <Alert
                                    variant="default"
                                    className="border-warning/50 bg-warning-muted"
                                  >
                                    <AlertCircle className="h-4 w-4 text-warning" />
                                    <AlertTitle className="text-warning">
                                      Processing Paused
                                    </AlertTitle>
                                    <AlertDescription className="text-muted-foreground">
                                      <p className="mb-2">{maxIterPart.maxIterations.message}</p>
                                      <p className="text-sm font-medium text-warning">
                                        {maxIterPart.maxIterations.suggestion}
                                      </p>
                                    </AlertDescription>
                                  </Alert>
                                )
                              })()}
                            {part.type === 'context-length' &&
                              (() => {
                                const ctxPart = part as ContextLengthPart
                                return (
                                  <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Conversation Too Long</AlertTitle>
                                    <AlertDescription>
                                      <p className="mb-2">{ctxPart.contextLength.message}</p>
                                      <p className="text-sm font-medium">
                                        {ctxPart.contextLength.suggestion}
                                      </p>
                                    </AlertDescription>
                                  </Alert>
                                )
                              })()}
                            {part.type === 'output-truncated' &&
                              (() => {
                                const truncPart = part as OutputTruncatedPart
                                return (
                                  <Alert
                                    variant="default"
                                    className="border-orange-500/50 bg-orange-500/10"
                                  >
                                    <Scissors className="h-4 w-4 text-orange-500" />
                                    <AlertTitle className="text-orange-500">
                                      Response Cut Short
                                    </AlertTitle>
                                    <AlertDescription className="text-muted-foreground">
                                      <p className="mb-2">{truncPart.outputTruncated.message}</p>
                                      <p className="text-sm font-medium text-orange-500">
                                        {truncPart.outputTruncated.suggestion}
                                      </p>
                                    </AlertDescription>
                                  </Alert>
                                )
                              })()}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed">
                        {message.role === 'user' ? (
                          <p className="whitespace-pre-wrap m-0">{message.content}</p>
                        ) : (
                          <Markdown>{message.content}</Markdown>
                        )}
                      </div>
                    )}

                    {/* Failed message - error + retry */}
                    {message.role === 'user' && failedMessageId === message.uuid && (
                      <div className="flex items-center gap-2 pt-2">
                        <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                        <span className="text-xs text-destructive">
                          {errorMessage || 'Failed to get response'}
                        </span>
                        {onRetry && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs gap-1"
                            onClick={() => onRetry(message.uuid)}
                          >
                            <RotateCcw className="h-3 w-3" />
                            Retry
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Message Actions - only for assistant messages */}
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-1 pt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-muted-foreground hover:text-foreground"
                              onClick={() => handleCopyMessage(message.content, message.uuid)}
                            >
                              {copiedMessageId === message.uuid ? (
                                <Check className="h-3.5 w-3.5" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Copy message</TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Streaming content */}
          {isStreaming && streamingContent.length > 0 && (
            <div className="bg-muted/20">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
                <div className="flex gap-4 items-start">
                  <Avatar className="h-8 w-8 shrink-0 mt-0.5">
                    <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                      <Bot className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">EcoSemantic</span>
                    </div>

                    <div className="space-y-3">
                      {streamingContent.map((item, index) => (
                        <div key={index}>
                          {item.type === 'text' && item.content && (
                            <div className="prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed">
                              <Markdown>{item.content}</Markdown>
                            </div>
                          )}
                          {item.type === 'tool_call' && item.toolCall && (
                            <ToolCallDisplay
                              toolCallId={item.toolCall.tool_call_id}
                              toolName={item.toolCall.tool_name}
                              arguments={item.toolCall.arguments}
                              result={item.toolCall.result}
                              success={item.toolCall.success}
                              isExecuting={item.toolCall.isExecuting}
                            />
                          )}
                          {item.type === 'max_iterations' && item.maxIterations && (
                            <Alert variant="default" className="border-warning/50 bg-warning-muted">
                              <AlertCircle className="h-4 w-4 text-warning" />
                              <AlertTitle className="text-warning">Processing Paused</AlertTitle>
                              <AlertDescription className="text-muted-foreground">
                                <p className="mb-2">{item.maxIterations.message}</p>
                                <p className="text-sm font-medium text-warning">
                                  {item.maxIterations.suggestion}
                                </p>
                              </AlertDescription>
                            </Alert>
                          )}
                          {item.type === 'context_length' && item.contextLength && (
                            <Alert variant="destructive">
                              <AlertCircle className="h-4 w-4" />
                              <AlertTitle>Conversation Too Long</AlertTitle>
                              <AlertDescription>
                                <p className="mb-2">{item.contextLength.message}</p>
                                <p className="text-sm font-medium">
                                  {item.contextLength.suggestion}
                                </p>
                              </AlertDescription>
                            </Alert>
                          )}
                          {item.type === 'output_truncated' && item.outputTruncated && (
                            <Alert
                              variant="default"
                              className="border-orange-500/50 bg-orange-500/10"
                            >
                              <Scissors className="h-4 w-4 text-orange-500" />
                              <AlertTitle className="text-orange-500">
                                Response Cut Short
                              </AlertTitle>
                              <AlertDescription className="text-muted-foreground">
                                <p className="mb-2">{item.outputTruncated.message}</p>
                                <p className="text-sm font-medium text-orange-500">
                                  {item.outputTruncated.suggestion}
                                </p>
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Streaming indicator */}
                    <TypingIndicator variant="dots" text="" className="pt-1" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator - shown when streaming starts but no content yet */}
          {isStreaming && streamingContent.length === 0 && (
            <div className="bg-muted/20">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
                <div className="flex gap-4 items-start">
                  <Avatar className="h-8 w-8 shrink-0 mt-0.5">
                    <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                      <Bot className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">EcoSemantic</span>
                    </div>
                    <TypingIndicator variant="dots" text="Thinking" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scroll anchor - with padding for floating input */}
          <div ref={bottomRef} className="h-32" />
        </div>
      </ScrollArea>

      {/* Scroll to bottom button */}
      <ScrollButton onClick={scrollToBottom} visible={showScrollButton && messages.length > 0} />
    </div>
  )
}
